import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";

// Fetches Cash in Bank from QuickBooks via /api/qb-bank-balance.
// Cheap, runs once on mount and exposes a refresh() callback for manual
// re-pulls. Returns { connected, totalBankBalance, accounts, fetchedAt,
// loading, error, refresh }.
export function useQbBankBalance({ enabled = true } = {}) {
  const [connected, setConnected] = useState(null);
  const [totalBankBalance, setTotalBankBalance] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const jwt = sessionData?.session?.access_token;
      if (!jwt) {
        setConnected(false);
        return;
      }
      const res = await fetch("/api/qb-bank-balance", {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (res.status === 404) {
        setConnected(false);
        return;
      }
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setConnected(true);
      setTotalBankBalance(Number(data.totalBankBalance) || 0);
      setAccounts(data.accounts || []);
      setFetchedAt(data.fetchedAt || null);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { refresh(); }, [refresh]);

  return { connected, totalBankBalance, accounts, fetchedAt, loading, error, refresh };
}